import { useEffect, useState } from "react";
import { crDate } from "@/lib/dateFormat";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import PageHeader from "@/components/PageHeader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, MessageSquareWarning, TrendingDown, MailCheck, Inbox } from "lucide-react";

const REASON_COLORS = {
  price: "bg-rose-100 text-rose-700 border-rose-200",
  no_time: "bg-amber-100 text-amber-700 border-amber-200",
  expectations_not_met: "bg-orange-100 text-orange-700 border-orange-200",
  changed_school: "bg-violet-100 text-violet-700 border-violet-200",
  achieved_goal: "bg-emerald-100 text-emerald-700 border-emerald-200",
  teacher_or_group_issue: "bg-red-100 text-red-700 border-red-200",
  other: "bg-slate-100 text-slate-700 border-slate-200",
};

function fmtDate(iso) {
  if (!iso) return "—";
  return crDate(iso) || iso;
}

export default function ChurnFeedbacks() {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all"); // all | submitted | pending

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === "submitted") params.submitted = true;
      else if (filter === "pending") params.submitted = false;
      const [list, st] = await Promise.all([
        api.get("/churn-feedbacks", { params }),
        api.get("/churn-feedbacks/stats"),
      ]);
      setRows(list.data || []);
      setStats(st.data || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const maxReasonCount = stats ? Math.max(1, ...Object.values(stats.by_reason || {})) : 1;
  const sortedReasons = stats
    ? Object.entries(stats.by_reason).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <div className="space-y-6" data-testid="churn-feedbacks-page">
      <PageHeader icon={MessageSquareWarning} title={t("churn_feedbacks")} />

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-5 border-slate-200">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center">
              <MailCheck className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">{t("churn_total_sent")}</div>
              <div data-testid="churn-stat-sent" className="text-2xl font-bold text-slate-900">{stats?.total_sent ?? "—"}</div>
            </div>
          </div>
        </Card>
        <Card className="p-5 border-slate-200">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center">
              <Inbox className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">{t("churn_total_submitted")}</div>
              <div data-testid="churn-stat-submitted" className="text-2xl font-bold text-slate-900">{stats?.total_submitted ?? "—"}</div>
            </div>
          </div>
        </Card>
        <Card className="p-5 border-slate-200">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-amber-50 text-amber-700 flex items-center justify-center">
              <TrendingDown className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">{t("churn_response_rate")}</div>
              <div data-testid="churn-stat-rate" className="text-2xl font-bold text-slate-900">
                {stats?.response_rate != null ? `${stats.response_rate}%` : "—"}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Reasons bar chart */}
      <Card className="p-5 border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-900">{t("churn_top_reasons")}</h3>
        </div>
        {sortedReasons.length === 0 ? (
          <div className="text-sm text-slate-500 py-4">{t("churn_no_responses_yet")}</div>
        ) : (
          <div className="space-y-2.5">
            {sortedReasons.map(([reason, count]) => {
              const pct = (count / maxReasonCount) * 100;
              return (
                <div key={reason} data-testid={`churn-bar-${reason}`} className="flex items-center gap-3">
                  <div className="text-xs text-slate-700 w-56 shrink-0 truncate">{t(`churn_reason_${reason}`)}</div>
                  <div className="flex-1 h-7 bg-slate-100 rounded-md overflow-hidden relative">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-md transition-all"
                      style={{ width: `${pct}%` }}
                    />
                    <div className="absolute inset-0 flex items-center justify-end pr-2">
                      <span className="text-xs font-semibold text-slate-700">{count}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Responses table */}
      <div>
        <Tabs value={filter} onValueChange={setFilter} className="mb-3">
          <TabsList data-testid="churn-filter-tabs">
            <TabsTrigger value="all" data-testid="churn-filter-all">{t("churn_all_responses")}</TabsTrigger>
            <TabsTrigger value="submitted" data-testid="churn-filter-submitted">{t("churn_filter_submitted")}</TabsTrigger>
            <TabsTrigger value="pending" data-testid="churn-filter-pending">{t("churn_filter_pending")}</TabsTrigger>
          </TabsList>
        </Tabs>

        <Card className="border-slate-200">
          {loading ? (
            <div className="p-10 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">{t("churn_empty")}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("churn_col_student")}</TableHead>
                  <TableHead>{t("churn_col_status")}</TableHead>
                  <TableHead>{t("churn_col_reasons")}</TableHead>
                  <TableHead>{t("churn_col_comment")}</TableHead>
                  <TableHead>{t("churn_col_sent")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`churn-row-${r.id}`}>
                    <TableCell>
                      <div className="font-medium text-slate-900">{r.student_name || "—"}</div>
                      <div className="text-xs text-slate-500">{r.student_email}</div>
                    </TableCell>
                    <TableCell>
                      {r.submitted ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100">
                          {t("churn_status_responded")}
                        </Badge>
                      ) : r.email_sent ? (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100">
                          {t("churn_status_pending")}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-slate-300 text-slate-600">
                          {t("churn_status_email_skipped")}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.reasons?.length ? (
                        <div className="flex flex-wrap gap-1.5 max-w-md">
                          {r.reasons.map((reason) => (
                            <span
                              key={reason}
                              className={`text-[10px] font-medium px-2 py-0.5 rounded border ${REASON_COLORS[reason] || REASON_COLORS.other}`}
                            >
                              {t(`churn_reason_${reason}`)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.comment ? (
                        <div className="text-xs text-slate-700 max-w-sm leading-relaxed line-clamp-3">{r.comment}</div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-xs text-slate-700">{fmtDate(r.created_at)}</div>
                      {r.submitted_at && (
                        <div className="text-[10px] text-emerald-600">↳ {fmtDate(r.submitted_at)}</div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>
    </div>
  );
}
